'use client';

import React from 'react';
import Link from 'next/link';

const JOURNEY_STAGES = [
  { id: 'keys-to-kulli-haal', label: 'Keys to Kulli Haal', short: 'KEYS', href: '/keys-to-kulli-haal', type: 'main', step: 1 },
  { id: 'bridge-environment-design', label: 'Bridge 01', short: 'B01', href: '/bridge-environment-design', type: 'bridge', step: 2 },
  { id: 'ayat-in-creation', label: 'Ayat in Creation', short: 'AYAT', href: '/ayat-in-creation', type: 'main', step: 3 },
  { id: 'bridge-irl-application', label: 'Bridge 02', short: 'B02', href: '/bridge-irl-application', type: 'bridge', step: 4 },
  { id: 'inner-reality-lens', label: 'Inner Reality Lens', short: 'LENS', href: '/inner-reality-lens', type: 'main', step: 5 },
  { id: 'irl-lens', label: 'IRL Lens', short: 'IRL', href: '/irl-lens', type: 'bridge', step: 6 },
  { id: 'bridge-resource-guide', label: 'Bridge 03', short: 'B03', href: '/bridge-resource-guide', type: 'bridge', step: 7 },
  { id: 'muslim-resource-guide', label: 'Muslim Resource Guide', short: 'GUIDE', href: '/muslim-resource-guide', type: 'main', step: 8 },
];

interface JourneyNavProps {
  currentPage: string;
}

export default function JourneyNav({ currentPage }: JourneyNavProps) {
  const currentIndex = JOURNEY_STAGES.findIndex(s => s.id === currentPage);
  const currentStage = JOURNEY_STAGES[currentIndex];

  return (
    <div className="sticky top-0 z-40 border-b" style={{ background: 'rgba(28,43,37,0.97)', borderColor: 'rgba(139,105,20,0.2)', backdropFilter: 'blur(16px)' }}>
      {/* Breadcrumb */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2 py-2 overflow-x-auto scrollbar-hide">
          <Link href="/homepage" className="font-mono text-xs transition-colors whitespace-nowrap flex-shrink-0" style={{ color: 'rgba(139,105,20,0.6)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(179,138,42,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(139,105,20,0.6)'; }}
          >
            THE HAAL PROJECT
          </Link>
          {/* Star separator */}
          <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0" style={{ opacity: 0.4 }}>
            <polygon points="6,0 7.5,4.5 12,4.5 8.5,7 9.5,12 6,9 2.5,12 3.5,7 0,4.5 4.5,4.5" fill="#B38A2A" />
          </svg>
          <span className="font-mono text-xs whitespace-nowrap flex-shrink-0" style={{ color: 'rgba(196,149,106,0.7)' }}>JOURNEY</span>
          <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0" style={{ opacity: 0.4 }}>
            <polygon points="6,0 7.5,4.5 12,4.5 8.5,7 9.5,12 6,9 2.5,12 3.5,7 0,4.5 4.5,4.5" fill="#B38A2A" />
          </svg>
          <span className="font-mono text-xs whitespace-nowrap flex-shrink-0" style={{ color: '#F5F0E8' }}>{currentStage?.label ?? currentPage}</span>
        </div>
      </div>

      {/* Progress Arc */}
      <div className="border-t" style={{ borderColor: 'rgba(139,105,20,0.1)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-0 py-2 overflow-x-auto scrollbar-hide">
            {JOURNEY_STAGES.map((stage, i) => {
              const isActive = stage.id === currentPage;
              const isPast = i < currentIndex;
              const isBridge = stage.type === 'bridge';

              return (
                <React.Fragment key={stage.id}>
                  {i > 0 && (
                    <div className={`h-px flex-1 min-w-[8px] transition-colors`}
                      style={{ background: isPast || isActive ? 'rgba(179,138,42,0.5)' : 'rgba(139,105,20,0.15)' }}
                    />
                  )}
                  <Link
                    href={stage.href}
                    className={`flex-shrink-0 flex flex-col items-center gap-0.5 group transition-all ${
                      isBridge ? 'px-1' : 'px-2'
                    }`}
                  >
                    <div className={`transition-all ${
                      isBridge
                        ? `w-2 h-2 rotate-45 border ${
                            isActive ? 'border-yellow-400' : isPast ? 'border-yellow-700' : 'border-green-800 group-hover:border-yellow-700'
                          }`
                        : `w-3 h-3 rounded-full border ${
                            isActive ? 'border-yellow-400' : isPast ? 'border-yellow-700' : 'border-green-800 group-hover:border-yellow-700'
                          }`
                    }`}
                    style={{
                      background: isActive ? '#B38A2A' : isPast ? 'rgba(139,105,20,0.5)' : 'transparent',
                      borderColor: isActive ? '#B38A2A' : isPast ? 'rgba(139,105,20,0.5)' : 'rgba(44,74,62,0.6)',
                    }}
                    />
                    {!isBridge && (
                      <span className={`font-mono text-[9px] tracking-widest uppercase whitespace-nowrap transition-colors`}
                        style={{ color: isActive ? '#F5F0E8' : isPast ? 'rgba(179,138,42,0.5)' : 'rgba(44,74,62,0.8)' }}
                      >
                        {stage.short}
                      </span>
                    )}
                  </Link>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
